import './modal.css';
import type { ButtonHandle } from './button';

export interface ModalOptions {
  title?: string;
  body: HTMLElement | string;
  actions?: ButtonHandle[];
  dismissible?: boolean;
  onDismiss?: () => void;
}

export interface ModalHandle {
  readonly el: HTMLElement;
  show(): void;
  hide(): void;
  setBody(body: HTMLElement | string): void;
  destroy(): void;
}

// Module-level stack of open modals — topmost (last) is the one Escape dismisses.
const openStack: Array<{ el: HTMLElement; dismiss: () => void }> = [];
let keyListenerInstalled = false;

function installKeyListener() {
  if (keyListenerInstalled) return;
  keyListenerInstalled = true;
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const top = openStack[openStack.length - 1];
    if (top) top.dismiss();
  });
}

export function createModal(opts: ModalOptions): ModalHandle {
  installKeyListener();
  const dismissible = opts.dismissible !== false;

  const el = document.createElement('div');
  el.className = 'fg-modal';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');

  const card = document.createElement('div');
  card.className = 'fg-modal__card';
  el.appendChild(card);

  if (opts.title) {
    const titleEl = document.createElement('h3');
    titleEl.className = 'fg-modal__title';
    titleEl.textContent = opts.title;
    card.appendChild(titleEl);
  }

  const bodyEl = document.createElement('div');
  bodyEl.className = 'fg-modal__body';
  setBodyContent(bodyEl, opts.body);
  card.appendChild(bodyEl);

  if (opts.actions && opts.actions.length > 0) {
    const actionsEl = document.createElement('div');
    actionsEl.className = 'fg-modal__actions';
    for (const action of opts.actions) actionsEl.appendChild(action.el);
    card.appendChild(actionsEl);
  }

  function setBodyContent(target: HTMLElement, content: HTMLElement | string) {
    target.replaceChildren();
    if (typeof content === 'string') target.textContent = content;
    else target.appendChild(content);
  }

  function dismiss() {
    if (!dismissible) return;
    hide();
    opts.onDismiss?.();
  }

  function onBackdropClick(e: MouseEvent) {
    if (e.target === el) dismiss();
  }
  el.addEventListener('click', onBackdropClick);

  function show() {
    el.classList.add('fg-modal--open');
    if (!openStack.find((m) => m.el === el)) {
      openStack.push({ el, dismiss });
    }
    const focusable = card.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();
  }

  function hide() {
    el.classList.remove('fg-modal--open');
    const idx = openStack.findIndex((m) => m.el === el);
    if (idx !== -1) openStack.splice(idx, 1);
  }

  return {
    el,
    show,
    hide,
    setBody(content) {
      setBodyContent(bodyEl, content);
    },
    destroy() {
      el.removeEventListener('click', onBackdropClick);
      const idx = openStack.findIndex((m) => m.el === el);
      if (idx !== -1) openStack.splice(idx, 1);
      el.remove();
    },
  };
}
